import MapWithControls from '@app/components/map2/MapWithControls';
import { MappingService } from '@app/api/mapping';
import { MapDefinition } from '@app/api/mapping/types';
import { useMemo, useState, useEffect } from 'react';
import { MapStateProvider } from '@app/components/map2/MapStateProvider';
import { useDemo } from '@app/components/demo/DemoProvider';
import { fetchAndUpdateAvailableForecasts } from '@app/components/map2/availableForecastsCache';

export default function MapViewPage() {
  const mapDefinition: MapDefinition = MappingService.availableMaps[0];
  const [isLoading, setIsLoading] = useState(true);
  const [latestInitTime, setLatestInitTime] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isDemo } = useDemo();

  useEffect(() => {
    const fetchLatestInitTime = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const availableForecasts = await MappingService.getAvailableForecasts(isDemo);

        const ept2Forecasts = availableForecasts.forecasts['ept2'];
        if (ept2Forecasts && ept2Forecasts.length > 0) {
          // Get the latest init time (assuming they are sorted, otherwise we'd need to sort them)
          const latest = ept2Forecasts[0];
          setLatestInitTime(latest);
        } else {
          setError('No forecasts available for ept2 model');
        }
      } catch (err) {
        setError('Failed to load available forecasts');
        console.error('Error fetching available forecasts:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatestInitTime();
  }, [isDemo]);

  // Fetch available forecasts for all models on page load
  useEffect(() => {
    fetchAndUpdateAvailableForecasts(isDemo).catch((err) => {
      console.error('Failed to load available forecasts:', err);
      // Non-blocking error - UI will show models as disabled
    });
  }, [isDemo]);

  const dataSource = useMemo(
    () => ({
      model: 'ept2',
      forecast: latestInitTime || 'latest', // fallback to 'latest' if loading fails
    }),
    [latestInitTime],
  );

  if (isLoading) {
    return (
      <div className="relative h-full w-full overflow-hidden flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          <p className="text-gray-600">Loading forecast data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative h-full w-full overflow-hidden flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="text-red-600 text-center">
            <p className="font-semibold">Error loading data</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Full-screen map */}
      <div className="absolute inset-0">
        <MapStateProvider initialDataSource={dataSource} initialMap={mapDefinition}>
          <MapWithControls allowSwitchDataSource={true} />
        </MapStateProvider>
      </div>
    </div>
  );
}
